import './StrangerProfilePage.scss';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import React, { useState, useEffect, useCallback, memo, useContext } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import ActivityIndicator from 'components/ActivityIndicator/ActivityIndicator';
import { fetchUser } from 'models/user/fetchUser';
import { fetchPublicUser } from 'models/user/fetchPublicUser';
import useUserAndToken from 'core/useUserAndToken';
import { isError } from 'models/core/error/IError';
import ChatInput from '../MessagesPage/ChatInput';
import { REGISTER_USER, HOME_PATH, EXPLORER_PATH } from 'models/Paths';
import PublicAppbarComponent from 'pages/Landingpage/components/PublicAppbarComponent';
import StrangerImageGallery from '../components/StrangerImageGallery';
import StrangerProfileHero from './components/StrangerProfileHero';
import StrangerProfileActionButtons from './components/StrangerProfileActionButtons';
import StrangerProfileInfoCard from './components/StrangerProfileInfoCard';
import ThemeContext from 'theme/ThemeContext';
import { IStrangerUser } from 'models/user/IStrangerUser/IStrangerUser';

export const StrangerProfilePage = memo(() => {
    const { strangerId, id: routeId } = useParams<{ strangerId: string; id?: string }>();
    const history = useHistory();
    const location = useLocation();
    const { user, token } = useUserAndToken();
    const [strangerUser, setStrangerUser] = useState<IStrangerUser | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [currentKey, setCurrentKey] = useState(0);
    const profileId = routeId;
    const { type } = useContext(ThemeContext);

    const filteredPictures = (strangerUser?.Pictures || []).filter((element) => element.Art !== 2);

    const handleBack = () => history.push(EXPLORER_PATH);

    const handleClick = (index: number) => {
        setCurrentKey(index);
        setOpenDialog(true);
    };

    const shouldForceRegisterOnClick = !token && location.pathname.startsWith('/profile') && !location.pathname.startsWith('/stranger-profile');
    const redirectToRegister = useCallback(() => {
        history.push(REGISTER_USER, { from: location.pathname });
    }, [history, location.pathname]);

    const fetchProfile = useCallback(() => {
        setIsLoading(true);
        const fetchFn =
            user && token && profileId ? fetchUser.bind(null, user.Userid, profileId, token, user) : fetchPublicUser.bind(null, strangerId);

        fetchFn().then((res) => {
            if (!isError(res)) {
                setStrangerUser(res[0]);
            }
            setIsLoading(false);
        });
    }, [user, token, profileId, strangerId]);

    useEffect(() => {
        fetchProfile();
    }, [fetchProfile]);

    if (profileId === 'support' || strangerId === 'support') {
        history.push(HOME_PATH);
        return null;
    }

    const themeClass = type === 'dark' ? 'stranger-profile--dark' : '';

    return (
        <>
            {!token && <PublicAppbarComponent />}

            <div className={`stranger-profile ${themeClass}`}>
                {isLoading ? (
                    <div className="stranger-profile__loading">
                        <ActivityIndicator />
                    </div>
                ) : (
                    <>
                        <div className="stranger-profile__hero-wrapper">
                            <StrangerProfileHero
                                pictures={filteredPictures}
                                strangerUser={strangerUser}
                                onPictureClick={handleClick}
                            />
                            {strangerUser && (
                                <StrangerProfileActionButtons
                                    strangerUser={strangerUser}
                                    token={token}
                                    user={user}
                                    isLiked={strangerUser.IsLiked}
                                />
                            )}
                        </div>

                        {strangerUser && (
                            <div className="stranger-profile__content-wrapper">
                                <StrangerProfileInfoCard
                                    strangerUser={strangerUser}
                                    token={token}
                                    onClose={handleBack}
                                    blurLocation={!token}
                                    pictures={filteredPictures}
                                    onPictureClick={handleClick}
                                />
                            </div>
                        )}

                        {!user || !token ? (
                            <div className="stranger-profile__guest-chat" onClick={redirectToRegister}>
                                <ChatInput
                                    dialogId={strangerUser?.Profilid ?? ''}
                                    user={user}
                                    partnerId={strangerUser?.Profilid ?? ''}
                                    onSend={redirectToRegister}
                                    userName={strangerUser?.Username ?? ''}
                                />
                            </div>
                        ) : null}

                        {strangerUser ? (
                            <StrangerImageGallery
                                currentKey={currentKey}
                                pictures={filteredPictures}
                                open={openDialog}
                                onClose={() => setOpenDialog(false)}
                                strangerUser={strangerUser}
                                user={user}
                                token={token}
                                onPurchaseFinished={fetchProfile}
                            />
                        ) : null}
                    </>
                )}

                {shouldForceRegisterOnClick && (
                    <div
                        aria-label="Zur Registrierung"
                        onClick={redirectToRegister}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                redirectToRegister();
                            }
                        }}
                        role="button"
                        tabIndex={0}
                        style={{
                            position: 'absolute',
                            inset: 0,
                            zIndex: 10,
                            cursor: 'pointer',
                            backgroundColor: 'transparent',
                        }}
                    />
                )}

            </div>
        </>
    );
});

export default StrangerProfilePage;
